import { parseError } from './errors';

/**
 * Run a write call safely:
 *   1) callStatic dry-run to surface a clear revert reason.
 *   2) Send the real transaction with a manual gas limit fallback
 *      (some RPCs — including MST's mario endpoint — occasionally return
 *      500s on eth_estimateGas even when the tx is fine).
 *   3) Await the receipt.
 *
 * Usage:
 *   const rc = await sendTx(logicW, 'claimIncome', [], { notify, label: 'Claim' });
 *
 * options:
 *   notify:  optional notification API (info/success/error)
 *   label:   short human label shown in info/success toasts
 *   gasBump: multiplier for the fallback gas limit (default 1.3)
 *   value:   ETH value to send (rare)
 */
export const sendTx = async (contract, method, args = [], opts = {}) => {
  const { notify, label, gasBump = 1.3, value } = opts;

  if (!contract) throw new Error('Contract not initialized (wallet or network not ready)');
  if (typeof contract[method] !== 'function') {
    throw new Error(`Method ${method} not found on contract`);
  }

  const overrides = value ? { value } : {};

  // 1) Dry run — this reverts with the same reason a real tx would.
  //    Bubbles up "TF" / "NIL" / "PRICE" / "OL" etc. cleanly.
  try {
    await contract.callStatic[method](...args, overrides);
  } catch (e) {
    throw e;
  }

  // 2) Try normal estimation-based send. If the RPC 500s (mariorpc has done this),
  //    fall back to a manual gas limit derived from estimateGas via provider directly,
  //    or a safe hard-coded ceiling.
  let tx;
  try {
    tx = await contract[method](...args, overrides);
  } catch (sendErr) {
    // If it's clearly a gas-estimation RPC hiccup, retry with manual gas.
    const msg = (sendErr && (sendErr.message || '')).toLowerCase();
    const isRpcHiccup =
      msg.includes('rpc request failed') ||
      msg.includes('eth_estimategas') ||
      msg.includes('failed to fetch');

    if (!isRpcHiccup) throw sendErr;

    // Retry with a manual gasLimit — a generous ceiling; safe for these paths.
    tx = await contract[method](...args, { ...overrides, gasLimit: 1_500_000 });
  }

  if (notify && label) notify.info(`${label}: submitted`);
  const receipt = await tx.wait();
  if (notify && label) notify.success(`${label}: confirmed`);
  return receipt;
};

/**
 * Same as sendTx but returns cleanly parsed error messages instead of throwing.
 * Returns { ok: true, receipt } or { ok: false, error: string }.
 */
export const trySendTx = async (contract, method, args, opts) => {
  try {
    const receipt = await sendTx(contract, method, args, opts);
    return { ok: true, receipt };
  } catch (e) {
    return { ok: false, error: parseError(e) };
  }
};
